import { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  useTranslation,
  useLocale,
  type Locale,
} from "./contexts/LocaleContext";
import { useTheme } from "./contexts/ThemeContext";
import { authenticatedFetch } from "./config/api";
import {
  Info,
  ArrowLeft,
  DollarSign,
  TrendingUp,
  Users,
  PieChart,
  BarChart,
  Calendar,
  FileText,
  Loader2,
} from "lucide-react";
import Toggle from "./Toggle";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";
// ECB rates utility and currency labels
import {
  getExchangeRatesFrom,
  SUPPORTED_ECB_CODES,
  ExchangeErrorCode,
} from "./utils/ecbRates";
import { CHF_CONVERSIONS } from "./utils/chfStaticRates";
import { currencyNames } from "./currencyNames";
import {
  getCurrencyLabel,
  getSortedCurrencies,
  getLocalizedCurrencyName,
} from "./utils/currencyLocalization";
import { sanitizeInput } from "./utils/sanitize";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

type InvoiceListItem = {
  id: string;
  invoiceNo: string;
  clientName: string;
  description: string;
  amount: number;
  currency: string;
  issueDate: string;
  dueDate: string;
  status: "paid" | "pending" | "overdue";
  createdAt: string;
  downloadedAt?: string;
};

type Expense = {
  id: string;
  name: string;
  category: string;
  date: string;
  amount: number;
  currency: string;
  note?: string;
  createdAt: string;
  updatedAt: string;
};

type ExchangeRates = {
  [key: string]: number;
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center">
    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
  </div>
);

export default function Statistics() {
  const location = useLocation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const t = useTranslation();
  const { locale } = useLocale();
  const { theme } = useTheme();
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [baseCurrency, setBaseCurrency] = useState("CZK");
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>({});
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showArpuInfo, setShowArpuInfo] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | "all">("all");
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(true);
  const [includePending, setIncludePending] = useState(true); // Include pending invoices by default

  // Global period selection - default to current year start, end = today
  const now = new Date();
  const currentYear = now.getFullYear();
  const todayStr = `${currentYear}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const [globalStartDate, setGlobalStartDate] = useState<string>(
    `${currentYear}-01-01`
  );
  const [globalEndDate, setGlobalEndDate] = useState<string>(todayStr);
  const [globalAllTime, setGlobalAllTime] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "custom" | "currentMonth" | "previousMonth" | "previousYear" | "allTime"
  >("custom");

  // ARPU/MRR all time toggle (keeping separately if user still wants it or just use global)
  // Actually the prompt says "instead of many inputs - make 1 global", so I will remove others.

  // Load invoices and expenses from backend
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingInvoices(true);
        setIsLoadingExpenses(true);

        const [invoicesRes, expensesRes] = await Promise.all([
          authenticatedFetch("/api/invoices-full"),
          authenticatedFetch("/api/expenses"),
        ]);

        // Handle 401 - redirect to login
        if (invoicesRes.status === 401 || expensesRes.status === 401) {
          const redirectPath = location.pathname + location.search;
          const loginPath = lang ? `/${lang}/login` : "/login";
          navigate(`${loginPath}?redirect=${encodeURIComponent(redirectPath)}`);
          return;
        }

        if (invoicesRes.ok) {
          const fullInvoices = await invoicesRes.json();

          // Convert backend format to InvoiceListItem format
          const convertedInvoices: InvoiceListItem[] = fullInvoices
            .map((inv: any) => {
              if (inv.draft) {
                const computedAmount = Array.isArray(inv.draft.items)
                  ? inv.draft.items.reduce(
                      (sum: number, item: any) =>
                        sum + (item?.amount || 0) * (item?.quantity || 1),
                      0
                    )
                  : undefined;
                return {
                  id: inv.internalId,
                  invoiceNo: inv.draft.invoiceNo || "",
                  clientName: inv.draft.buyerName || "",
                  description:
                    (inv.draft.items?.length > 0
                      ? inv.draft.items[0]?.description
                      : "") || "",
                  amount:
                    computedAmount === 0 ? 0 : computedAmount || undefined,
                  currency: inv.draft.currency || "",
                  issueDate: inv.draft.issueDate || "",
                  dueDate: inv.draft.dueDate || "",
                  status: (inv.draft.status || "pending") as
                    | "paid"
                    | "pending"
                    | "overdue",
                  createdAt: inv.createdAt || new Date().toISOString(),
                  downloadedAt: inv.metadata?.lastDownloadedAt,
                } as InvoiceListItem;
              }
              return null;
            })
            .filter((inv: any): inv is InvoiceListItem => inv !== null);

          setInvoices(convertedInvoices);
        }

        if (expensesRes.ok) {
          const expensesList = await expensesRes.json();
          setExpenses(expensesList);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoadingInvoices(false);
        setIsLoadingExpenses(false);
      }
    };
    loadData();
  }, [navigate]);

  // Build currency options: all from main list + ECB supported (merged)
  const supportedCodes = useMemo(
    () => Array.from(SUPPORTED_ECB_CODES).sort(),
    []
  );
  const allCodes = useMemo(() => {
    const codesFromMain = Object.values(currencyNames) as string[];
    const codes = Array.from(
      new Set([...(codesFromMain || []), ...supportedCodes])
    );

    const topCodes = ["CZK", "EUR", "USD", "GBP"];

    const topList = codes
      .filter((c) => topCodes.includes(c))
      .sort((a, b) => topCodes.indexOf(a) - topCodes.indexOf(b));

    const otherList = codes
      .filter((c) => !topCodes.includes(c))
      .sort((a, b) => {
        const keyA = Object.keys(currencyNames).find(
          (k) => currencyNames[k] === a
        );
        const keyB = Object.keys(currencyNames).find(
          (k) => currencyNames[k] === b
        );
        const nameA = keyA
          ? getLocalizedCurrencyName(keyA, locale as Locale)
          : a;
        const nameB = keyB
          ? getLocalizedCurrencyName(keyB, locale as Locale)
          : b;
        return nameA.localeCompare(nameB, locale as Locale);
      });

    return [...topList, ...otherList];
  }, [supportedCodes, locale]);

  // Fetch exchange rates via ECB
  useEffect(() => {
    const fetchRates = async () => {
      setIsLoadingRates(true);
      try {
        const res = await getExchangeRatesFrom(baseCurrency);
        // res.rates = targetPerBase. To convert amounts in target currency to base we divide by (target per base)
        setExchangeRates(res.rates || {});
      } catch (e: any) {
        if (e?.code === ExchangeErrorCode.UnsupportedCurrency) {
          // ignore unsupported currency
        } else {
          console.error("Error fetching ECB rates:", e);
        }
        setExchangeRates({});
      } finally {
        setIsLoadingRates(false);
      }
    };
    fetchRates();
  }, [baseCurrency]);

  // Load default base currency from backend settings once
  useEffect(() => {
    (async () => {
      try {
        const r = await authenticatedFetch("/api/settings");
        // Skip if unauthorized - user will be redirected by ProtectedRoute
        if (r.status === 401) return;
        if (!r.ok) return;
        const data = await r.json();
        if (data?.defaultCurrency) setBaseCurrency(data.defaultCurrency);
      } catch {
        // Silently ignore errors - use default currency
      }
    })();
  }, []);

  // Close tooltips when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".info-tooltip-wrapper")) {
        setShowInfo(false);
        setShowArpuInfo(false);
      }
    };

    if (showInfo || showArpuInfo) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showInfo, showArpuInfo]);

  // Convert amount to base currency
  const convertToBaseCurrency = useCallback(
    (amount: number, currency: string): number => {
      if (!amount || currency === baseCurrency) return amount;

      // 1) Try ECB rate: target per base
      const rateTargetPerBase = exchangeRates[currency];
      if (Number.isFinite(rateTargetPerBase)) {
        // amount is in target currency; base = amount / (target per base)
        return amount / rateTargetPerBase;
      }

      // 2) Fallback via CHF static conversions
      // CHF_CONVERSIONS[x] = x per 1 CHF
      const tPerChf = CHF_CONVERSIONS[currency];
      const bPerChf = CHF_CONVERSIONS[baseCurrency];

      if (Number.isFinite(tPerChf) && Number.isFinite(bPerChf)) {
        // amount_in_base = amount_in_T * (CHF per T) * (B per CHF)
        // CHF per T = 1 / (T per CHF)
        const chfPerT = 1 / (tPerChf as number);
        return amount * chfPerT * (bPerChf as number);
      }

      // As a last resort, return original amount (no conversion available)
      return amount;
    },
    [baseCurrency, exchangeRates]
  );

  // Helper: filter invoices based on includePending setting
  const getRelevantInvoices = useCallback(
    (invoiceList: InvoiceListItem[]) => {
      return invoiceList.filter((inv) => {
        if (includePending) {
          return inv.status === "paid" || inv.status === "pending";
        }
        return inv.status === "paid";
      });
    },
    [includePending]
  );

  // Helper: filter invoices by period
  const filterByPeriod = useCallback(
    (
      invoiceList: any[],
      startDate: string,
      endDate: string,
      allTime: boolean
    ) => {
      if (allTime) return invoiceList;

      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      return invoiceList.filter((inv) => {
        const invoiceDate = new Date(inv.issueDate || inv.date);
        return invoiceDate >= start && invoiceDate <= end;
      });
    },
    []
  );

  // Calculate revenue by client
  const revenueByClient = useMemo(() => {
    const clientMap = new Map<string, number>();
    const relevantInvoices = getRelevantInvoices(invoices);
    const filteredInvoices = filterByPeriod(
      relevantInvoices,
      globalStartDate,
      globalEndDate,
      globalAllTime
    );

    filteredInvoices.forEach((invoice) => {
      const convertedAmount = convertToBaseCurrency(
        invoice.amount,
        invoice.currency
      );
      const current = clientMap.get(invoice.clientName) || 0;
      clientMap.set(invoice.clientName, current + convertedAmount);
    });

    const clientList = Array.from(clientMap.entries())
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount);

    const totalRevenueInPeriod = clientList.reduce(
      (sum, item) => sum + item.amount,
      0
    );
    const uniqueClients = new Set(
      filteredInvoices.map((inv) => inv.clientName).filter(Boolean)
    ).size;

    return {
      clients: clientList,
      totalRevenue: totalRevenueInPeriod,
      uniqueClients: uniqueClients,
    };
  }, [
    invoices,
    globalStartDate,
    globalEndDate,
    globalAllTime,
    convertToBaseCurrency,
    filterByPeriod,
    getRelevantInvoices,
  ]);

  // Calculate revenue by currency
  const revenueByCurrency = useMemo(() => {
    const currencyMap = new Map<string, number>();
    const relevantInvoices = getRelevantInvoices(invoices);
    const filteredInvoices = filterByPeriod(
      relevantInvoices,
      globalStartDate,
      globalEndDate,
      globalAllTime
    );

    filteredInvoices.forEach((invoice) => {
      const convertedAmount = convertToBaseCurrency(
        invoice.amount,
        invoice.currency
      );
      const current = currencyMap.get(invoice.currency) || 0;
      currencyMap.set(invoice.currency, current + convertedAmount);
    });

    const currencyList = Array.from(currencyMap.entries())
      .map(([currency, amount]) => ({ currency, amount }))
      .sort((a, b) => b.amount - a.amount);

    const totalRevenueInPeriod = currencyList.reduce(
      (sum, item) => sum + item.amount,
      0
    );

    return {
      currencies: currencyList,
      totalRevenue: totalRevenueInPeriod,
    };
  }, [
    invoices,
    globalStartDate,
    globalEndDate,
    globalAllTime,
    convertToBaseCurrency,
    filterByPeriod,
    getRelevantInvoices,
  ]);

  // Calculate expenses by category
  const expensesByCategory = useMemo(() => {
    const categoryMap = new Map<string, number>();
    const filteredExpenses = filterByPeriod(
      expenses as any,
      globalStartDate,
      globalEndDate,
      globalAllTime
    );

    filteredExpenses.forEach((expense: any) => {
      const convertedAmount = convertToBaseCurrency(
        expense.amount,
        expense.currency
      );
      const current = categoryMap.get(expense.category) || 0;
      categoryMap.set(expense.category, current + convertedAmount);
    });

    const categoryList = Array.from(categoryMap.entries())
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount);

    const totalExpenseInPeriod = categoryList.reduce(
      (sum, item) => sum + item.amount,
      0
    );

    return {
      categories: categoryList,
      totalExpense: totalExpenseInPeriod,
    };
  }, [
    expenses,
    globalStartDate,
    globalEndDate,
    globalAllTime,
    convertToBaseCurrency,
    filterByPeriod,
  ]);

  // Calculate expenses by currency
  const expensesByCurrency = useMemo(() => {
    const currencyMap = new Map<string, number>();
    const filteredExpenses = filterByPeriod(
      expenses as any,
      globalStartDate,
      globalEndDate,
      globalAllTime
    );

    filteredExpenses.forEach((expense: any) => {
      const convertedAmount = convertToBaseCurrency(
        expense.amount,
        expense.currency
      );
      const current = currencyMap.get(expense.currency) || 0;
      currencyMap.set(expense.currency, current + convertedAmount);
    });

    const currencyList = Array.from(currencyMap.entries())
      .map(([currency, amount]) => ({ currency, amount }))
      .sort((a, b) => b.amount - a.amount);

    const totalExpenseInPeriod = currencyList.reduce(
      (sum, item) => sum + item.amount,
      0
    );

    return {
      currencies: currencyList,
      totalExpense: totalExpenseInPeriod,
    };
  }, [
    expenses,
    globalStartDate,
    globalEndDate,
    globalAllTime,
    convertToBaseCurrency,
    filterByPeriod,
  ]);

  // Calculate timeline data (income and expenses)
  const timelineData = useMemo(() => {
    const monthlyIncome = new Map<string, number>();
    const monthlyExpenses = new Map<string, number>();

    const relevantInvoices = getRelevantInvoices(invoices);

    relevantInvoices.forEach((invoice) => {
      const date = new Date(invoice.issueDate);
      const year = date.getFullYear();

      if (selectedYear === "all" || year === selectedYear) {
        const monthKey = `${year}-${String(date.getMonth() + 1).padStart(
          2,
          "0"
        )}`;
        const convertedAmount = convertToBaseCurrency(
          invoice.amount,
          invoice.currency
        );
        const current = monthlyIncome.get(monthKey) || 0;
        monthlyIncome.set(monthKey, current + convertedAmount);
      }
    });

    expenses.forEach((expense) => {
      const date = new Date(expense.date);
      const year = date.getFullYear();

      if (selectedYear === "all" || year === selectedYear) {
        const monthKey = `${year}-${String(date.getMonth() + 1).padStart(
          2,
          "0"
        )}`;
        const convertedAmount = convertToBaseCurrency(
          expense.amount,
          expense.currency
        );
        const current = monthlyExpenses.get(monthKey) || 0;
        monthlyExpenses.set(monthKey, current + convertedAmount);
      }
    });

    // Get all month keys
    const incomeKeys = Array.from(monthlyIncome.keys());
    const expenseKeys = Array.from(monthlyExpenses.keys());
    const allMonths = Array.from(
      new Set([...incomeKeys, ...expenseKeys])
    ).sort();

    return allMonths.map((month) => ({
      month,
      income: monthlyIncome.get(month) || 0,
      expenses: monthlyExpenses.get(month) || 0,
    }));
  }, [
    invoices,
    expenses,
    selectedYear,
    convertToBaseCurrency,
    getRelevantInvoices,
  ]);

  // Calculate total revenue
  const totalRevenue = useMemo(() => {
    const relevantInvoices = getRelevantInvoices(invoices);
    const filteredInvoices = filterByPeriod(
      relevantInvoices,
      globalStartDate,
      globalEndDate,
      globalAllTime
    );
    return filteredInvoices.reduce((sum, invoice) => {
      return sum + convertToBaseCurrency(invoice.amount, invoice.currency);
    }, 0);
  }, [
    invoices,
    globalStartDate,
    globalEndDate,
    globalAllTime,
    convertToBaseCurrency,
    filterByPeriod,
    getRelevantInvoices,
  ]);

  // Calculate total expenses
  const totalExpensesSum = useMemo(() => {
    const filteredExpenses = filterByPeriod(
      expenses as any,
      globalStartDate,
      globalEndDate,
      globalAllTime
    );
    return filteredExpenses.reduce((sum: number, expense: any) => {
      return sum + convertToBaseCurrency(expense.amount, expense.currency);
    }, 0);
  }, [
    expenses,
    globalStartDate,
    globalEndDate,
    globalAllTime,
    convertToBaseCurrency,
    filterByPeriod,
  ]);

  // Calculate Net Income
  const netIncome = useMemo(() => {
    return totalRevenue - totalExpensesSum;
  }, [totalRevenue, totalExpensesSum]);

  // Helper: Get number of months in period
  const monthsInPeriod = useMemo(() => {
    if (globalAllTime) {
      if (timelineData.length === 0) return 1;
      return timelineData.length;
    }

    const start = new Date(globalStartDate);
    const end = new Date(globalEndDate);

    // If dates are in the same month and year, it's 1 month
    if (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth()
    ) {
      return 1;
    }

    // Otherwise calculate the difference in months
    const yearDiff = end.getFullYear() - start.getFullYear();
    const monthDiff = end.getMonth() - start.getMonth();
    const totalMonths = yearDiff * 12 + monthDiff + 1;

    return Math.max(1, totalMonths);
  }, [globalStartDate, globalEndDate, globalAllTime, timelineData]);

  // Calculate Average Monthly Revenue
  const avgMonthlyRevenue = useMemo(() => {
    return totalRevenue / monthsInPeriod;
  }, [totalRevenue, monthsInPeriod]);

  // Calculate Average Monthly Expenses
  const avgMonthlyExpenses = useMemo(() => {
    return totalExpensesSum / monthsInPeriod;
  }, [totalExpensesSum, monthsInPeriod]);

  // Get available years
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    invoices.forEach((invoice) => {
      const date = new Date(invoice.issueDate);
      if (!isNaN(date.getTime())) years.add(date.getFullYear());
    });
    expenses.forEach((expense) => {
      const date = new Date(expense.date);
      if (!isNaN(date.getTime())) years.add(date.getFullYear());
    });
    if (years.size === 0) years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [invoices, expenses]);

  // Calculate ARPU for selected period
  const arpu = useMemo(() => {
    let invoicesInPeriod: InvoiceListItem[];

    if (globalAllTime) {
      // Filter all invoices based on includePending setting
      invoicesInPeriod = invoices.filter((inv) => {
        if (includePending) {
          if (inv.status !== "paid" && inv.status !== "pending") return false;
        } else {
          if (inv.status !== "paid") return false;
        }
        if (!inv.amount || inv.amount === 0) return false;
        if (!inv.clientName) return false;
        return true;
      });
    } else {
      const startDate = new Date(globalStartDate);
      const endDate = new Date(globalEndDate);
      endDate.setHours(23, 59, 59, 999);

      // Filter invoices within the period based on includePending setting
      invoicesInPeriod = invoices.filter((inv) => {
        // Status filter: if includePending, accept paid and pending; otherwise only paid
        if (includePending) {
          if (inv.status !== "paid" && inv.status !== "pending") return false;
        } else {
          if (inv.status !== "paid") return false;
        }
        if (!inv.amount || inv.amount === 0) return false; // Skip invoices with no amount
        if (!inv.clientName) return false; // Skip invoices without client name
        const invoiceDate = new Date(inv.issueDate);
        return invoiceDate >= startDate && invoiceDate <= endDate;
      });
    }

    if (invoicesInPeriod.length === 0) return 0;

    // Calculate total revenue in period
    const totalRevenueInPeriod = invoicesInPeriod.reduce((sum, inv) => {
      return sum + convertToBaseCurrency(inv.amount || 0, inv.currency);
    }, 0);

    // Count unique customers in period
    const uniqueCustomers = new Set(
      invoicesInPeriod.map((inv) => inv.clientName).filter(Boolean)
    );
    const customerCount = uniqueCustomers.size;

    if (customerCount === 0) return 0;

    return totalRevenueInPeriod / customerCount;
  }, [
    invoices,
    globalStartDate,
    globalEndDate,
    includePending,
    globalAllTime,
    convertToBaseCurrency,
  ]);

  // Calculate MRR
  const mrr = useMemo(() => {
    // Get all unique customers (all time) based on includePending setting
    const relevantInvoices = invoices.filter((inv) => {
      if (includePending) {
        return (
          (inv.status === "paid" || inv.status === "pending") &&
          inv.amount &&
          inv.amount > 0 &&
          inv.clientName
        );
      } else {
        return (
          inv.status === "paid" &&
          inv.amount &&
          inv.amount > 0 &&
          inv.clientName
        );
      }
    });
    const uniqueCustomers = new Set(
      relevantInvoices.map((inv) => inv.clientName).filter(Boolean)
    );
    const totalCustomers = uniqueCustomers.size;

    if (totalCustomers === 0 || arpu === 0) return 0;

    // MRR = Total customers × ARPU
    return totalCustomers * arpu;
  }, [invoices, arpu, includePending]);

  // Calculate status distribution for chart
  const statusChartData = useMemo(() => {
    let invoicesInPeriod: InvoiceListItem[];

    if (globalAllTime) {
      invoicesInPeriod = invoices;
    } else {
      const startDate = new Date(globalStartDate);
      const endDate = new Date(globalEndDate);
      endDate.setHours(23, 59, 59, 999); // Include full end date

      // Filter invoices within the period
      invoicesInPeriod = invoices.filter((inv) => {
        const invoiceDate = new Date(inv.issueDate);
        return invoiceDate >= startDate && invoiceDate <= endDate;
      });
    }

    const paidInvoices = invoicesInPeriod.filter(
      (inv) => inv.status === "paid"
    );
    const pendingInvoices = invoicesInPeriod.filter(
      (inv) => inv.status === "pending"
    );
    const overdueInvoices = invoicesInPeriod.filter(
      (inv) => inv.status === "overdue"
    );

    const paidCount = paidInvoices.length;
    const pendingCount = pendingInvoices.length;
    const overdueCount = overdueInvoices.length;
    const total = invoicesInPeriod.length;

    // Calculate revenue amounts for each status
    const paidAmount = paidInvoices.reduce((sum, inv) => {
      return sum + convertToBaseCurrency(inv.amount || 0, inv.currency);
    }, 0);

    const pendingAmount = pendingInvoices.reduce((sum, inv) => {
      return sum + convertToBaseCurrency(inv.amount || 0, inv.currency);
    }, 0);

    const overdueAmount = overdueInvoices.reduce((sum, inv) => {
      return sum + convertToBaseCurrency(inv.amount || 0, inv.currency);
    }, 0);

    return {
      paid: paidCount,
      pending: pendingCount,
      overdue: overdueCount,
      total: total,
      paidPercent: total > 0 ? (paidCount / total) * 100 : 0,
      pendingPercent: total > 0 ? (pendingCount / total) * 100 : 0,
      overduePercent: total > 0 ? (overdueCount / total) * 100 : 0,
      paidAmount: paidAmount,
      pendingAmount: pendingAmount,
      overdueAmount: overdueAmount,
    };
  }, [
    invoices,
    globalStartDate,
    globalEndDate,
    globalAllTime,
    convertToBaseCurrency,
  ]);

  // Helper functions
  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat("cs-CZ", {
      style: "currency",
      currency: baseCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number, total: number): string => {
    if (total === 0) return "0%";
    return `${Math.round((value / total) * 100)}%`;
  };

  const formatMonth = (monthKey: string): string => {
    const [year, month] = monthKey.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1);
    // Use locale from context: 'en' -> 'en-US', 'ru' -> 'ru-RU', 'cs' -> 'cs-CZ'
    const localeMap: Record<string, string> = {
      en: "en-US",
      ru: "ru-RU",
      cs: "cs-CZ",
    };
    const dateLocale = localeMap[locale || "en"] || "en-US";
    return date.toLocaleDateString(dateLocale, {
      month: "short",
      year: "numeric",
    });
  };

  const changeYear = (direction: "prev" | "next") => {
    if (selectedYear === "all") {
      setSelectedYear(availableYears[0] || new Date().getFullYear());
      return;
    }

    const currentIndex = availableYears.indexOf(selectedYear as number);
    if (direction === "prev" && currentIndex < availableYears.length - 1) {
      setSelectedYear(availableYears[currentIndex + 1]);
    } else if (direction === "next" && currentIndex > 0) {
      setSelectedYear(availableYears[currentIndex - 1]);
    }
  };

  const handleTabChange = (
    tab: "currentMonth" | "previousMonth" | "previousYear" | "allTime"
  ) => {
    const now = new Date();
    let start = "";
    let end = "";

    if (tab === "currentMonth") {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      start = firstDay.toISOString().split("T")[0];
      end = lastDay.toISOString().split("T")[0];
      setGlobalAllTime(false);
    } else if (tab === "previousMonth") {
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      start = firstDay.toISOString().split("T")[0];
      end = lastDay.toISOString().split("T")[0];
      setGlobalAllTime(false);
    } else if (tab === "previousYear") {
      const lastYear = now.getFullYear() - 1;
      start = `${lastYear}-01-01`;
      end = `${lastYear}-12-31`;
      setGlobalAllTime(false);
    } else if (tab === "allTime") {
      setGlobalAllTime(true);
    }

    if (tab !== "allTime") {
      setGlobalStartDate(start);
      setGlobalEndDate(end);
    }
    setActiveTab(tab);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Compact Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 transition-colors">
            {t.statistics.title}
          </h1>
        </div>

        {/* Compact Controls Row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
          {/* Global Date Filter */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/50 dark:border-slate-700 shadow-sm p-3 transition-colors col-span-1 md:col-span-12 lg:col-span-6"
          >
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { id: "currentMonth", label: t.statistics.currentMonth },
                  { id: "previousMonth", label: t.statistics.previousMonth },
                  { id: "previousYear", label: t.statistics.previousYear },
                  { id: "allTime", label: t.statistics.allTime },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() =>
                      handleTabChange(
                        tab.id as
                          | "currentMonth"
                          | "previousMonth"
                          | "previousYear"
                          | "allTime"
                      )
                    }
                    className={`px-3 py-1.5 text-[10px] font-bold rounded-lg transition-colors border ${
                      activeTab === tab.id
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-slate-50 text-slate-600 hover:bg-slate-100 dark:bg-slate-700/50 dark:text-slate-400 dark:hover:bg-slate-700 border-slate-200 dark:border-slate-600"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100 dark:border-slate-700/50">
                <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t.statistics.period}:
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={globalStartDate}
                    onChange={(e) => {
                      setGlobalStartDate(sanitizeInput(e.target.value));
                      setActiveTab("custom");
                      setGlobalAllTime(false);
                    }}
                    disabled={globalAllTime}
                    className="w-[115px] px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all disabled:opacity-50"
                  />
                  <span className="text-slate-400 dark:text-slate-600 font-bold">
                    —
                  </span>
                  <input
                    type="date"
                    value={globalEndDate}
                    onChange={(e) => {
                      setGlobalEndDate(sanitizeInput(e.target.value));
                      setActiveTab("custom");
                      setGlobalAllTime(false);
                    }}
                    disabled={globalAllTime}
                    className="w-[115px] px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all disabled:opacity-50"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Include Pending Toggle */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/50 dark:border-slate-700 shadow-sm p-3 transition-colors col-span-1 md:col-span-6 lg:col-span-3"
          >
            <label
              className="flex items-center gap-2 cursor-pointer h-full"
              style={{ margin: "0 !important" }}
            >
              <Toggle checked={includePending} onChange={setIncludePending} />
              <span className="text-sm font-bold text-slate-700 dark:text-slate-300 transition-colors">
                {t.statistics.includePending}
              </span>
            </label>
          </motion.div>

          {/* Currency Selector */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/50 dark:border-slate-700 shadow-sm p-3 transition-colors col-span-1 md:col-span-6 lg:col-span-3"
          >
            <div className="flex flex-col gap-2 h-full justify-center">
              <div className="flex items-center justify-between">
                <label
                  className="text-[10px] font-bold text-slate-500 dark:text-slate-400 whitespace-nowrap transition-colors uppercase tracking-wider"
                  style={{ margin: "0 !important" }}
                >
                  {t.statistics.baseCurrency}:
                </label>
                <div className="relative info-tooltip-wrapper shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowInfo(!showInfo);
                    }}
                    onMouseEnter={() => setShowInfo(true)}
                    onMouseLeave={() => setShowInfo(false)}
                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors border-0 bg-transparent outline-none"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                  <AnimatePresence>
                    {showInfo && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute top-full right-0 mt-2 w-72 md:w-80 p-4 bg-slate-900 dark:bg-slate-800 text-white text-xs rounded-xl shadow-2xl z-[100] pointer-events-none"
                      >
                        <div className="whitespace-pre-wrap leading-relaxed">
                          {t.statistics.infoTooltip}
                        </div>
                        <div className="absolute -top-1 right-2 w-2 h-2 bg-slate-900 dark:bg-slate-800 rotate-45" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={baseCurrency}
                  onChange={(e) => setBaseCurrency(e.target.value)}
                  className="flex-1 px-3 py-1.5 text-sm font-bold border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-slate-700 dark:text-white transition-colors iv-select outline-none"
                >
                  {allCodes.map((code) => {
                    const key = Object.keys(currencyNames).find(
                      (k) =>
                        currencyNames[k as keyof typeof currencyNames] === code
                    );
                    const label = key
                      ? getCurrencyLabel(key, code, locale as Locale)
                      : code;
                    return (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    );
                  })}
                </select>
                {isLoadingRates && (
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">
                    {t.statistics.loadingRates}
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Metrics Grid - Total Revenue, Total Expenses, etc. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {[
            {
              id: "totalRevenue",
              title: t.statistics.totalRevenue,
              icon: (
                <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              ),
              value: isLoadingRates ? (
                <LoadingSpinner />
              ) : (
                formatAmount(totalRevenue)
              ),
              valueColor: "text-emerald-600 dark:text-emerald-400",
              delay: 0.02,
              subtitle: "",
              info: null,
              showInfo: false,
              onInfoToggle: () => {},
            },
            {
              id: "totalExpenses",
              title: t.statistics.totalExpenses,
              icon: (
                <TrendingUp className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              ),
              value:
                isLoadingRates || isLoadingExpenses ? (
                  <LoadingSpinner />
                ) : (
                  formatAmount(totalExpensesSum)
                ),
              valueColor: "text-orange-600 dark:text-orange-400",
              delay: 0.03,
              subtitle: "",
              info: null,
              showInfo: false,
              onInfoToggle: () => {},
            },
            {
              id: "netIncome",
              title: t.statistics.netIncome,
              icon: (
                <PieChart className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              ),
              value:
                isLoadingRates || isLoadingExpenses ? (
                  <LoadingSpinner />
                ) : (
                  formatAmount(netIncome)
                ),
              valueColor:
                netIncome >= 0
                  ? "text-blue-600 dark:text-blue-400"
                  : "text-rose-600 dark:text-rose-400",
              delay: 0.04,
              subtitle: "",
              info: null,
              showInfo: false,
              onInfoToggle: () => {},
            },
            {
              id: "avgMonthlyRevenue",
              title: t.statistics.avgMonthlyRevenue,
              icon: (
                <Calendar className="w-4 h-4 text-emerald-500 dark:text-emerald-300" />
              ),
              value: isLoadingRates ? (
                <LoadingSpinner />
              ) : (
                formatAmount(avgMonthlyRevenue)
              ),
              valueColor: "text-emerald-500 dark:text-emerald-300",
              delay: 0.05,
              subtitle: "",
              info: null,
              showInfo: false,
              onInfoToggle: () => {},
            },
            {
              id: "avgMonthlyExpenses",
              title: t.statistics.avgMonthlyExpenses,
              icon: (
                <Calendar className="w-4 h-4 text-orange-500 dark:text-orange-300" />
              ),
              value:
                isLoadingRates || isLoadingExpenses ? (
                  <LoadingSpinner />
                ) : (
                  formatAmount(avgMonthlyExpenses)
                ),
              valueColor: "text-orange-500 dark:text-orange-300",
              delay: 0.06,
              subtitle: "",
              info: null,
              showInfo: false,
              onInfoToggle: () => {},
            },
          ].map((card) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: card.delay }}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/50 dark:border-slate-700 shadow-sm p-4 flex flex-col min-h-[140px] transition-colors"
            >
              <div className="flex items-center justify-between mb-3 shrink-0">
                <div className="flex items-center gap-2">
                  {card.icon}
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 m-0">
                    {card.title}
                  </h3>
                </div>
                {card.info && (
                  <div className="relative info-tooltip-wrapper">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        card.onInfoToggle?.();
                      }}
                      className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors border-0 bg-transparent outline-none"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                    <AnimatePresence>
                      {card.showInfo && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute top-full right-0 mt-2 w-64 p-3 bg-slate-900 dark:bg-slate-800 text-white text-[10px] rounded-xl shadow-2xl z-[100] pointer-events-none"
                        >
                          {card.info}
                          <div className="absolute -top-1 right-2 w-2 h-2 bg-slate-900 dark:bg-slate-800 rotate-45" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              <div className="flex-1 flex flex-col justify-center">
                <div className="text-center py-1">
                  <div
                    className={`text-xl sm:text-2xl font-black ${card.valueColor} truncate`}
                  >
                    {card.value}
                  </div>
                  {card.subtitle && (
                    <div className="text-[10px] text-slate-400 mt-1 font-medium uppercase tracking-wider">
                      {card.subtitle}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
          {/* Status Distribution Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/50 dark:border-slate-700 shadow-sm p-3 transition-colors"
          >
            <div className="mb-4">
              <div className="flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                <h3
                  className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wider"
                  style={{ margin: "0 !important" }}
                >
                  {t.statistics.statusDistribution}
                </h3>
              </div>
            </div>
            {statusChartData.total > 0 ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-48 h-48">
                  <Doughnut
                    data={{
                      labels: [
                        `${t.invoiceList.status.paid} (${statusChartData.paid})`,
                        `${t.invoiceList.status.pending} (${statusChartData.pending})`,
                        `${t.invoiceList.status.overdue} (${statusChartData.overdue})`,
                      ],
                      datasets: [
                        {
                          data: [
                            statusChartData.paid,
                            statusChartData.pending,
                            statusChartData.overdue,
                          ],
                          backgroundColor: [
                            "rgba(34, 197, 94, 0.8)", // Green for paid
                            "rgba(245, 158, 11, 0.8)", // Yellow for pending
                            "rgba(239, 68, 68, 0.8)", // Red for overdue
                          ],
                          borderColor: [
                            "rgba(34, 197, 94, 1)",
                            "rgba(245, 158, 11, 1)",
                            "rgba(239, 68, 68, 1)",
                          ],
                          borderWidth: 3,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      cutout: "60%",
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const value = context.parsed;
                              const total = statusChartData.total;
                              const percent =
                                total > 0
                                  ? ((value / total) * 100).toFixed(1)
                                  : "0";
                              const statusIndex = context.dataIndex;
                              let amount = 0;
                              if (statusIndex === 0)
                                amount = statusChartData.paidAmount;
                              else if (statusIndex === 1)
                                amount = statusChartData.pendingAmount;
                              else if (statusIndex === 2)
                                amount = statusChartData.overdueAmount;
                              return `${
                                context.label
                              }: ${percent}% • ${formatAmount(amount)}`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1.5 text-xs w-full">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0"></div>
                    <span className="font-semibold text-slate-900 dark:text-slate-100 text-[10px]">
                      {t.invoiceList.status.paid}:
                    </span>
                    <span className="text-slate-600 dark:text-slate-400 text-[10px]">
                      {statusChartData.paid} (
                      {statusChartData.paidPercent.toFixed(1)}%)
                    </span>
                    <span className="text-slate-600 dark:text-slate-400 text-[10px]">
                      • {formatAmount(statusChartData.paidAmount)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500 flex-shrink-0"></div>
                    <span className="font-semibold text-slate-900 dark:text-slate-100 text-[10px]">
                      {t.invoiceList.status.pending}:
                    </span>
                    <span className="text-slate-600 dark:text-slate-400 text-[10px]">
                      {statusChartData.pending} (
                      {statusChartData.pendingPercent.toFixed(1)}%)
                    </span>
                    <span className="text-slate-600 dark:text-slate-400 text-[10px]">
                      • {formatAmount(statusChartData.pendingAmount)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0"></div>
                    <span className="font-semibold text-slate-900 dark:text-slate-100 text-[10px]">
                      {t.invoiceList.status.overdue}:
                    </span>
                    <span className="text-slate-600 dark:text-slate-400 text-[10px]">
                      {statusChartData.overdue} (
                      {statusChartData.overduePercent.toFixed(1)}%)
                    </span>
                    <span className="text-slate-600 dark:text-slate-400 text-[10px]">
                      • {formatAmount(statusChartData.overdueAmount)}
                    </span>
                  </div>
                  <div className="pt-1.5 mt-1.5 border-t border-slate-200 dark:border-slate-700">
                    <span className="font-semibold text-slate-900 dark:text-slate-100 text-[10px]">
                      {t.statistics.total}:
                    </span>
                    <span className="text-slate-600 dark:text-slate-400 ml-1.5 text-[10px]">
                      {statusChartData.total}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500 dark:text-slate-400 text-xs">
                {t.statistics.noData}
              </div>
            )}
          </motion.div>

          {/* Revenue by Client Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/50 dark:border-slate-700 shadow-sm p-3 transition-colors"
          >
            <div className="mb-4">
              <div className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                <h3
                  className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wider"
                  style={{ margin: "0 !important" }}
                >
                  {t.statistics.revenueByClient}
                </h3>
              </div>
            </div>
            {revenueByClient.clients.length > 0 ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-48 h-48">
                  <Doughnut
                    data={{
                      labels: revenueByClient.clients.map((item) =>
                        item.name.length > 20
                          ? item.name.substring(0, 17) + "..."
                          : item.name
                      ),
                      datasets: [
                        {
                          data: revenueByClient.clients.map(
                            (item) => item.amount
                          ),
                          backgroundColor: [
                            "rgba(99, 102, 241, 0.8)",
                            "rgba(236, 72, 153, 0.8)",
                            "rgba(34, 197, 94, 0.8)",
                            "rgba(245, 158, 11, 0.8)",
                            "rgba(239, 68, 68, 0.8)",
                            "rgba(139, 92, 246, 0.8)",
                            "rgba(16, 185, 129, 0.8)",
                            "rgba(249, 115, 22, 0.8)",
                          ],
                          borderColor: [
                            "rgba(99, 102, 241, 1)",
                            "rgba(236, 72, 153, 1)",
                            "rgba(34, 197, 94, 1)",
                            "rgba(245, 158, 11, 1)",
                            "rgba(239, 68, 68, 1)",
                            "rgba(139, 92, 246, 1)",
                            "rgba(16, 185, 129, 1)",
                            "rgba(249, 115, 22, 1)",
                          ],
                          borderWidth: 3,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      cutout: "60%",
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const value = context.parsed;
                              if (value === null || value === undefined)
                                return "";
                              return `${context.label}: ${formatAmount(
                                value
                              )} (${formatPercent(
                                value,
                                revenueByClient.totalRevenue
                              )})`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1 max-h-32 overflow-y-auto w-full text-[10px]">
                  {revenueByClient.clients.map((item, index) => (
                    <div key={item.name} className="flex items-center gap-1.5">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: [
                            "rgba(99, 102, 241, 0.8)",
                            "rgba(236, 72, 153, 0.8)",
                            "rgba(34, 197, 94, 0.8)",
                            "rgba(245, 158, 11, 0.8)",
                            "rgba(239, 68, 68, 0.8)",
                            "rgba(139, 92, 246, 0.8)",
                            "rgba(16, 185, 129, 0.8)",
                            "rgba(249, 115, 22, 0.8)",
                          ][index % 8],
                        }}
                      ></div>
                      <div className="flex-1 min-w-0">
                        <div
                          className="font-semibold text-slate-900 dark:text-slate-100 truncate text-[10px]"
                          title={item.name}
                        >
                          {item.name}
                        </div>
                        <div className="text-slate-600 dark:text-slate-400 text-[10px]">
                          {formatAmount(item.amount)} (
                          {formatPercent(
                            item.amount,
                            revenueByClient.totalRevenue
                          )}
                          )
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="pt-1.5 mt-1.5 border-t border-slate-200 dark:border-slate-700">
                    <span className="font-semibold text-slate-900 dark:text-slate-100 text-[10px]">
                      {t.statistics.total}:
                    </span>
                    <span className="text-slate-600 dark:text-slate-400 ml-1.5 text-[10px]">
                      {revenueByClient.uniqueClients}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500 dark:text-slate-400 text-xs">
                {t.statistics.noData}
              </div>
            )}
          </motion.div>

          {/* Revenue by Currency Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/50 dark:border-slate-700 shadow-sm p-3 transition-colors"
          >
            <div className="mb-4">
              <div className="flex items-center gap-1.5">
                <PieChart className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <h3
                  className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wider"
                  style={{ margin: "0 !important" }}
                >
                  {t.statistics.revenueByCurrency}
                </h3>
              </div>
            </div>
            {revenueByCurrency.currencies.length > 0 ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-48 h-48">
                  <Doughnut
                    data={{
                      labels: revenueByCurrency.currencies.map(
                        (item) => item.currency
                      ),
                      datasets: [
                        {
                          data: revenueByCurrency.currencies.map(
                            (item) => item.amount
                          ),
                          backgroundColor: [
                            "rgba(16, 185, 129, 0.8)", // Emerald
                            "rgba(5, 150, 105, 0.8)", // Emerald dark
                            "rgba(52, 211, 153, 0.8)", // Emerald light
                            "rgba(4, 120, 87, 0.8)", // Emerald darker
                            "rgba(110, 231, 183, 0.8)", // Emerald lighter
                            "rgba(6, 95, 70, 0.8)", // Emerald darkest
                          ],
                          borderColor: [
                            "rgba(16, 185, 129, 1)",
                            "rgba(5, 150, 105, 1)",
                            "rgba(52, 211, 153, 1)",
                            "rgba(4, 120, 87, 1)",
                            "rgba(110, 231, 183, 1)",
                            "rgba(6, 95, 70, 1)",
                          ],
                          borderWidth: 3,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      cutout: "60%",
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const value = context.parsed;
                              if (value === null || value === undefined)
                                return "";
                              return `${context.label}: ${formatAmount(
                                value
                              )} (${formatPercent(
                                value,
                                revenueByCurrency.totalRevenue
                              )})`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1 max-h-32 overflow-y-auto w-full text-[10px]">
                  {revenueByCurrency.currencies.map((item, index) => (
                    <div
                      key={item.currency}
                      className="flex items-center gap-1.5"
                    >
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: [
                            "rgba(16, 185, 129, 0.8)",
                            "rgba(5, 150, 105, 0.8)",
                            "rgba(52, 211, 153, 0.8)",
                            "rgba(4, 120, 87, 0.8)",
                            "rgba(110, 231, 183, 0.8)",
                            "rgba(6, 95, 70, 0.8)",
                          ][index % 6],
                        }}
                      ></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 dark:text-slate-100 truncate text-[10px]">
                          {item.currency}
                        </div>
                        <div className="text-slate-600 dark:text-slate-400 text-[10px]">
                          {formatAmount(item.amount)} (
                          {formatPercent(
                            item.amount,
                            revenueByCurrency.totalRevenue
                          )}
                          )
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500 dark:text-slate-400 text-xs">
                {t.statistics.noData}
              </div>
            )}
          </motion.div>

          {/* Expenses by Category Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/50 dark:border-slate-700 shadow-sm p-3 transition-colors"
          >
            <div className="mb-4">
              <div className="flex items-center gap-1.5">
                <PieChart className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />
                <h3
                  className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wider"
                  style={{ margin: "0 !important" }}
                >
                  {t.statistics.expensesByCategory}
                </h3>
              </div>
            </div>
            {expensesByCategory.categories.length > 0 ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-48 h-48">
                  <Doughnut
                    data={{
                      labels: expensesByCategory.categories.map((item) => {
                        const catLabel =
                          (t as any).expenses.categories[item.name] ||
                          item.name;
                        return catLabel.length > 20
                          ? catLabel.substring(0, 17) + "..."
                          : catLabel;
                      }),
                      datasets: [
                        {
                          data: expensesByCategory.categories.map(
                            (item) => item.amount
                          ),
                          backgroundColor: [
                            "rgba(249, 115, 22, 0.8)", // Orange
                            "rgba(245, 158, 11, 0.8)", // Yellow
                            "rgba(234, 179, 8, 0.8)", // Amber
                            "rgba(251, 191, 36, 0.8)", // Amber light
                            "rgba(217, 119, 6, 0.8)", // Amber dark
                            "rgba(180, 83, 9, 0.8)", // Amber darker
                          ],
                          borderColor: [
                            "rgba(249, 115, 22, 1)",
                            "rgba(245, 158, 11, 1)",
                            "rgba(234, 179, 8, 1)",
                            "rgba(251, 191, 36, 1)",
                            "rgba(217, 119, 6, 1)",
                            "rgba(180, 83, 9, 1)",
                          ],
                          borderWidth: 3,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      cutout: "60%",
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const value = context.parsed;
                              if (value === null || value === undefined)
                                return "";
                              return `${context.label}: ${formatAmount(
                                value
                              )} (${formatPercent(
                                value,
                                expensesByCategory.totalExpense
                              )})`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1 max-h-32 overflow-y-auto w-full text-[10px]">
                  {expensesByCategory.categories.map((item, index) => (
                    <div key={item.name} className="flex items-center gap-1.5">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: [
                            "rgba(249, 115, 22, 0.8)",
                            "rgba(245, 158, 11, 0.8)",
                            "rgba(234, 179, 8, 0.8)",
                            "rgba(251, 191, 36, 0.8)",
                            "rgba(217, 119, 6, 0.8)",
                            "rgba(180, 83, 9, 0.8)",
                          ][index % 6],
                        }}
                      ></div>
                      <div className="flex-1 min-w-0">
                        <div
                          className="font-semibold text-slate-900 dark:text-slate-100 truncate text-[10px]"
                          title={
                            (t as any).expenses.categories[item.name] ||
                            item.name
                          }
                        >
                          {(t as any).expenses.categories[item.name] ||
                            item.name}
                        </div>
                        <div className="text-slate-600 dark:text-slate-400 text-[10px]">
                          {formatAmount(item.amount)} (
                          {formatPercent(
                            item.amount,
                            expensesByCategory.totalExpense
                          )}
                          )
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500 dark:text-slate-400 text-xs">
                {t.statistics.noData}
              </div>
            )}
          </motion.div>

          {/* Expenses by Currency Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.14 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/50 dark:border-slate-700 shadow-sm p-3 transition-colors"
          >
            <div className="mb-4">
              <div className="flex items-center gap-1.5">
                <PieChart className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                <h3
                  className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wider"
                  style={{ margin: "0 !important" }}
                >
                  {t.statistics.expensesByCurrency}
                </h3>
              </div>
            </div>
            {expensesByCurrency.currencies.length > 0 ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-48 h-48">
                  <Doughnut
                    data={{
                      labels: expensesByCurrency.currencies.map(
                        (item) => item.currency
                      ),
                      datasets: [
                        {
                          data: expensesByCurrency.currencies.map(
                            (item) => item.amount
                          ),
                          backgroundColor: [
                            "rgba(249, 115, 22, 0.8)",
                            "rgba(245, 158, 11, 0.8)",
                            "rgba(234, 179, 8, 0.8)",
                            "rgba(251, 191, 36, 0.8)",
                            "rgba(217, 119, 6, 0.8)",
                            "rgba(180, 83, 9, 0.8)",
                          ],
                          borderColor: [
                            "rgba(249, 115, 22, 1)",
                            "rgba(245, 158, 11, 1)",
                            "rgba(234, 179, 8, 1)",
                            "rgba(251, 191, 36, 1)",
                            "rgba(217, 119, 6, 1)",
                            "rgba(180, 83, 9, 1)",
                          ],
                          borderWidth: 3,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      cutout: "60%",
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const value = context.parsed;
                              if (value === null || value === undefined)
                                return "";
                              return `${context.label}: ${formatAmount(
                                value
                              )} (${formatPercent(
                                value,
                                expensesByCurrency.totalExpense
                              )})`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1 max-h-32 overflow-y-auto w-full text-[10px]">
                  {expensesByCurrency.currencies.map((item, index) => (
                    <div
                      key={item.currency}
                      className="flex items-center gap-1.5"
                    >
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: [
                            "rgba(249, 115, 22, 0.8)",
                            "rgba(245, 158, 11, 0.8)",
                            "rgba(234, 179, 8, 0.8)",
                            "rgba(251, 191, 36, 0.8)",
                            "rgba(217, 119, 6, 0.8)",
                            "rgba(180, 83, 9, 0.8)",
                          ][index % 6],
                        }}
                      ></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 dark:text-slate-100 truncate text-[10px]">
                          {item.currency}
                        </div>
                        <div className="text-slate-600 dark:text-slate-400 text-[10px]">
                          {formatAmount(item.amount)} (
                          {formatPercent(
                            item.amount,
                            expensesByCurrency.totalExpense
                          )}
                          )
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500 dark:text-slate-400 text-xs">
                {t.statistics.noData}
              </div>
            )}
          </motion.div>
        </div>

        {/* Timeline Chart - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/50 dark:border-slate-700 shadow-sm p-4 mb-4 transition-colors"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BarChart className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <h3
                className="text-sm font-semibold text-slate-900 dark:text-slate-100"
                style={{ margin: "0 !important" }}
              >
                {t.statistics.revenueTimeline}
              </h3>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => changeYear("prev")}
                className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                disabled={
                  selectedYear === "all" ||
                  availableYears.indexOf(selectedYear as number) ===
                    availableYears.length - 1
                }
              >
                <ArrowLeft className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300" />
              </button>

              <select
                value={selectedYear}
                onChange={(e) =>
                  setSelectedYear(
                    e.target.value === "all" ? "all" : parseInt(e.target.value)
                  )
                }
                className="px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-slate-700 dark:text-white iv-select transition-colors"
              >
                <option value="all">{t.statistics.allYears}</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              <button
                onClick={() => changeYear("next")}
                className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors rotate-180"
                disabled={
                  selectedYear === "all" ||
                  availableYears.indexOf(selectedYear as number) === 0
                }
              >
                <ArrowLeft className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300" />
              </button>
            </div>
          </div>

          {timelineData.length > 0 ? (
            <div className="h-64">
              <Line
                data={{
                  labels: timelineData.map((item) => formatMonth(item.month)),
                  datasets: [
                    {
                      label: `${t.statistics.income} (${baseCurrency})`,
                      data: timelineData.map((item) => item.income),
                      borderColor: "rgba(99, 102, 241, 1)", // Indigo
                      backgroundColor: "rgba(99, 102, 241, 0.1)",
                      borderWidth: 3,
                      fill: true,
                      tension: 0.4,
                      pointBackgroundColor: "rgba(99, 102, 241, 1)",
                      pointBorderColor: theme === "dark" ? "#1e293b" : "#fff",
                      pointBorderWidth: 2,
                      pointRadius: 6,
                      pointHoverRadius: 8,
                    },
                    {
                      label: `${t.statistics.expense} (${baseCurrency})`,
                      data: timelineData.map((item) => item.expenses),
                      borderColor: "rgba(249, 115, 22, 1)", // Orange
                      backgroundColor: "rgba(249, 115, 22, 0.1)",
                      borderWidth: 3,
                      fill: true,
                      tension: 0.4,
                      pointBackgroundColor: "rgba(249, 115, 22, 1)",
                      pointBorderColor: theme === "dark" ? "#1e293b" : "#fff",
                      pointBorderWidth: 2,
                      pointRadius: 6,
                      pointHoverRadius: 8,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: "top",
                      labels: {
                        usePointStyle: true,
                        padding: 20,
                        color: theme === "dark" ? "#fff" : "#000",
                        font: {
                          size: 11,
                          weight: "bold",
                        },
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const value = context.parsed.y;
                          if (value === null || value === undefined) return "";
                          return `${context.dataset.label}: ${formatAmount(
                            value
                          )}`;
                        },
                      },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color:
                          theme === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                      },
                      ticks: {
                        color:
                          theme === "dark"
                            ? "rgba(255, 255, 255, 0.7)"
                            : "rgba(0, 0, 0, 0.7)",
                        callback: function (value) {
                          return formatAmount(value as number);
                        },
                      },
                    },
                    x: {
                      grid: {
                        color:
                          theme === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                      },
                      ticks: {
                        color:
                          theme === "dark"
                            ? "rgba(255, 255, 255, 0.7)"
                            : "rgba(0, 0, 0, 0.7)",
                      },
                    },
                  },
                }}
              />
            </div>
          ) : (
            <div className="text-center py-6 text-slate-500 dark:text-slate-400 text-xs">
              {t.statistics.noData}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
